import { useEffect, useState } from 'react';
import { app } from "@microsoft/teams-js";
import { useDispatch, useSelector } from "react-redux";
import { rawUserRequest } from '@shared/services/Auth';
import { redirectTo, selectCurrentUser, setAuthLoading, setUser } from "../../store/auth.duck";
import {
  AuthType,
  GetCurrentAuthType,
  resetAllLoginStorages,
} from '@shared/utils/AuthUtils';
import { useMsal } from "@azure/msal-react";
import { isOnPublicPage } from "@app/App";
import { bootstrapAuth, requestCalendarAccess } from '@shared/auth/authMachine.duck';


export function WithTeamsAuth() {
  const dispatch = useDispatch();
  const [context, setContext] = useState<app.Context | null>(null);
  const user = useSelector(selectCurrentUser);
  const {instance} = useMsal();

  useEffect(() => {
    if (!context) {
      app.initialize().then(() => app.getContext()).then((ctx: app.Context) => {
        setContext(ctx);
        setTimeout(() => app.notifySuccess(), 10);
      }).catch(() => {
      // Teams SDK context is unavailable outside Teams — nothing to do.
    });
    }
  }, [context, dispatch]);

  useEffect(() => {
    if (!context || !!user) return;

    const requestUserToCheckAuth = async () => {
      const userResp = await rawUserRequest();
      dispatch(setUser(userResp));
    }

    const teamsAuthentication = async () => {
      try {
        await app.initialize();
        await (dispatch(bootstrapAuth({ source: 'app-mount' })) as any).unwrap();
        const userResp = await rawUserRequest();
        dispatch(setUser(userResp));
      } catch (e: any) {
        const isCancelled = e?.message?.includes?.('CancelledByUser') || e?.toString?.().includes?.('CancelledByUser');
        if (isCancelled) {
          console.warn(`[TeamsAuth] login cancelled by user`);
        }
        resetAllLoginStorages();
        if (!isOnPublicPage()) {
          dispatch(redirectTo('/'));
        }
      } finally {
        dispatch(setAuthLoading(false));
      }
    }

    const standardAuthentication = async () => {
      try {
        await requestUserToCheckAuth();
      } catch (e: any) {
        resetAllLoginStorages();
        if (!isOnPublicPage()) {
          dispatch(redirectTo('/'));
        }
      } finally {
        dispatch(setAuthLoading(false));
      }
    }

    if (GetCurrentAuthType() === AuthType.Standard) {
      standardAuthentication();
    } else {
      teamsAuthentication();
    }

  }, [instance, context, dispatch, user]);

  useEffect(() => {
    if (user?.companyEntity?.calendarSyncEnabled && user?.enableOutlookSync && user?.authType !== AuthType.Google) {
      dispatch(requestCalendarAccess({ source: 'auto' }));
    }
  }, [user, dispatch]);
}
